import { useMemo } from "react"
import { ComparisonView } from "routes/schedule/compare/comparisonView"
import { CompareTitleTop, ScheduleHeaderToolbar, UOMArea } from "slot-definitions"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { Frag } from "lib/@components/slot/frag"
import { Bound } from "lib/@components/binding/Bound"
import { ProjectScheduleCrumbs } from "authoring/routes/project/components/project-schedule-crumbs"
import noop from "lib/noop"
import { useParams } from "react-router"
import { If } from "lib/@components/switch"
import { useCompareSchedule } from "./lib/use-compare-schedule"
import { indexTasksInSchedule } from "routes/schedule/lib/index-tasks-in-schedule"
import { buildScheduleContent } from "authoring/routes/macro/lib/macro-conversion"
import { normalizeScheduleHtml } from "./lib/normalize-schedule-html"
import { collectMacroTokensFromSchedule } from "authoring/routes/macro/lib/macro-token-utils"
import { useEnrichedMacroMap } from "authoring/routes/macro/hooks/use-enriched-macro-map"
import { getExpandedSchedulePreview } from "./controller/get-expanded-schedule-preview"
import { refreshOnlyFor } from "lib/graphql/api"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { BothPanelWrapper } from "routes/schedule/compare/compare-two-impl"
import { AuthoringCompareBothPanel } from "./authoring-compare-both-panel"
import { prepareScheduleForAuthoringComparison } from "./lib/prepare-schedule-for-authoring-comparison"
import "./code-comparitor"

export function CompareDraft() {
    const { source, projectId } = useParams()
    ScheduleHeaderToolbar.usePlug(replaceAllWith(<Frag />))
    CompareTitleTop.usePlug(replaceAllWith(<Frag />))
    BothPanelWrapper.usePlug(replaceAllWith(<AuthoringCompareBothPanel />))
    UOMArea.usePlug(replaceAllWith(<Frag />))

    const { loading: loadingSource, schedule: scheduleSource } = useCompareSchedule(source)

    // Server expands both linked fields (componentRef/linkedFields → actual values)
    // and macro tokens, so the comparison shows real content differences.
    const expandedPreviewData = getExpandedSchedulePreview.useResults(
        { projectId, scheduleId: source },
        refreshOnlyFor(createDraftId(projectId, source)),
        { fetchPolicy: "no-cache" }
    )
    const expandedDestination = expandedPreviewData?.schedule

    const tokenIds = useMemo(collectTokenIds, [expandedDestination, scheduleSource])
    const { macroMap, loading: macrosLoading } = useEnrichedMacroMap({
        tokenIds,
        retrieveDeleted: false,
    })

    const displayDestination = useMemo(prepareDestination, [expandedDestination])
    const displaySource = useMemo(prepareSource, [macroMap, scheduleSource])

    if (displaySource?.tasks?.length) indexTasksInSchedule(displaySource)
    if (displayDestination?.tasks?.length) indexTasksInSchedule(displayDestination)

    const loading = loadingSource || !expandedPreviewData || macrosLoading
    if (loading) {
        return null
    }
    const { code, title } = displayDestination || {}
    return (
        <If truthy={displayDestination && displaySource}>
            <Bound
                comparisonReadOnly
                compareFormatting
                Crumbs={ProjectScheduleCrumbs}
                target={{ code, title }}
                isAuthoringPdfExportSource={true}
                projectId={projectId}
                scheduleId={source}
                macroMap={macroMap}
            >
                <ComparisonView
                    $lazy
                    destination={[displayDestination, noop, noop, `Draft of ${title}`]}
                    source={[displaySource, noop, noop, `Last published version ${displaySource.version} of ${title}`]}
                />
            </Bound>
        </If>
    )

    function prepareDestination() {
        return prepareScheduleForAuthoringComparison(expandedDestination)
    }

    function prepareSource() {
        return prepareScheduleForAuthoringComparison(
            normalizeScheduleHtml(buildScheduleContent(scheduleSource, macroMap))
        )
    }

    function collectTokenIds() {
        const tokens = new Set()
        collectMacroTokensFromSchedule(expandedDestination, tokens)
        collectMacroTokensFromSchedule(scheduleSource, tokens)
        return [...tokens]
    }
}
